import React, { useEffect, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useTranslation, Trans } from 'react-i18next';
import { getIntlLocale } from '../../i18n';
import { Plus, Trash2, X, Save, ToggleLeft, ToggleRight } from 'lucide-react';
import theme from '../../theme';

// The self-hosted copy in public/fonts, referenced once from index.html.
// This used to be a Google Fonts URL injected per page, which disclosed every
// visitor's IP to a third party on every page load and contradicted the privacy
// policy's "no third-party requests" claim. Kept as a constant because several
// pages import it into an @import rule rather than a <link>.
const FONTS = '/fonts/fonts.css';

const EMPTY_FORM = {
  code: '', type: 'percentage', value: '', min_rental_days: '1', max_uses: '', expires_at: ''
};

export default function AdminDiscounts() {
  const { t } = useTranslation();
  // The expiry column was hardcoded to 'el-GR', so an admin working in English
  // still saw Greek-formatted dates. Follow the selected language instead.
  const dateLocale = getIntlLocale();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const link = document.createElement('link'); link.rel = 'stylesheet'; link.href = FONTS; document.head.appendChild(link);
    loadDiscounts();
    return () => document.head.removeChild(link);
  }, []);

  const loadDiscounts = () => {
    setLoading(true);
    api.get('/discounts')
      .then(setDiscounts)
      .catch(() => toast.error(t('admin.loadError')))
      .finally(() => setLoading(false));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/discounts', {
        ...form,
        value: parseFloat(form.value),
        min_rental_days: parseInt(form.min_rental_days) || 1,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      });
      toast.success(t('admin.discounts.created'));
      setShowForm(false);
      setForm(EMPTY_FORM);
      loadDiscounts();
    } catch (err) {
      toast.error(err.error || t('admin.discounts.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (d) => {
    try {
      await api.patch(`/discounts/${d.id}`, { active: !d.active });
      toast.success(d.active ? t('admin.discounts.deactivated') : t('admin.discounts.activated'));
      loadDiscounts();
    } catch {
      toast.error(t('admin.discounts.updateFailed'));
    }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(t('admin.discounts.deleteConfirm', { code: d.code }))) return;
    try {
      await api.delete(`/discounts/${d.id}`);
      toast.success(t('admin.discounts.deleted'));
      loadDiscounts();
    } catch {
      toast.error(t('admin.discounts.deleteFailed'));
    }
  };

  const usageLabel = (d) => {
    if (d.max_uses === null) return t('admin.discounts.uses', { count: d.uses_count });
    return t('admin.discounts.usesOf', { used: d.uses_count, max: d.max_uses });
  };

  // Single interpolated sentence — each clause is a complete, translatable unit.
  const previewValues = () => {
    const value = form.type === 'percentage'
      ? t('admin.discounts.previewValuePercent', { value: form.value })
      : t('admin.discounts.previewValueFixed', { value: form.value });
    const minDays = parseInt(form.min_rental_days) > 1
      ? t('admin.discounts.previewMinDays', { count: parseInt(form.min_rental_days) })
      : '';
    const uses = form.max_uses
      ? t('admin.discounts.previewMaxUses', { count: parseInt(form.max_uses) })
      : t('admin.discounts.previewUnlimited');
    return {
      code: form.code || t('admin.discounts.previewCodeFallback'),
      value,
      conditions: `${minDays}${uses}`,
    };
  };

  const typeLabel = (type) => type === 'percentage'
    ? t('admin.discounts.typePercentageShort')
    : t('admin.discounts.typeFixedShort');

  const isExpired = (d) => d.expires_at && new Date(d.expires_at) < new Date();

  return (
    <div style={{ minHeight: '100vh', background: theme.grey100, fontFamily: theme.font.body, display: 'flex' }}>
      <AdminSidebar />

      <div className="admin-main" style={{ padding: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40 }}>
          <div>
            <h1 style={{ fontFamily: theme.font.display, fontSize: 40, color: theme.ink, margin: 0 }}>{t('admin.discounts.title')}</h1>
            <p style={{ color: theme.grey400, margin: '4px 0 0' }}>{t('admin.discounts.count', { count: discounts.length })}</p>
          </div>
          <button onClick={() => setShowForm(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: theme.ink, color: theme.white, border: 'none', padding: '14px 24px', borderRadius: 2, cursor: 'pointer', fontSize: 13, letterSpacing: '0.1em' }}>
            <Plus size={16} /> {t('admin.discounts.newCode')}
          </button>
        </div>

        <div style={{ background: theme.white, border: `1px solid ${theme.creamBone}`, borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: theme.cream }}>
                {['colCode', 'colType', 'colValue', 'colMinDays', 'colUsage', 'colExpires', 'colStatus', 'colActions'].map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', color: theme.grey400, fontWeight: 500, fontSize: 12, letterSpacing: '0.08em' }}>{t(`admin.discounts.${h}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: theme.gold }}>{t('admin.discounts.loading')}</td></tr>
              ) : discounts.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 48, textAlign: 'center', color: theme.grey400 }}>{t('admin.discounts.noDiscounts')}</td></tr>
              ) : discounts.map(d => {
                const expired = isExpired(d);
                return (
                  <tr key={d.id} style={{ borderBottom: `1px solid ${theme.creamDeep}`, opacity: (!d.active || expired) ? 0.55 : 1 }}>
                    <td style={{ padding: '16px' }}>
                      <span style={{ fontFamily: theme.font.mono, background: theme.creamDeep, padding: '4px 10px', borderRadius: 4, fontSize: 13, letterSpacing: '0.05em', color: theme.ink }}>{d.code}</span>
                    </td>
                    <td style={{ padding: '16px', color: theme.grey600, fontSize: 13 }}>{typeLabel(d.type)}</td>
                    <td style={{ padding: '16px', fontFamily: theme.font.display, fontSize: 22, color: theme.ink }}>
                      {d.type === 'percentage' ? `${parseFloat(d.value).toFixed(0)}%` : `€${parseFloat(d.value).toFixed(2)}`}
                    </td>
                    <td style={{ padding: '16px', color: theme.grey600, fontSize: 13 }}>{t('admin.discounts.minDaysShort', { count: d.min_rental_days })}</td>
                    <td style={{ padding: '16px', color: theme.grey600, fontSize: 13 }}>{usageLabel(d)}</td>
                    <td style={{ padding: '16px', fontSize: 12, color: expired ? theme.red : theme.grey400 }}>
                      {d.expires_at ? new Date(d.expires_at).toLocaleDateString(dateLocale) : '—'}
                      {expired && <span style={{ display: 'block', color: theme.red, fontSize: 10 }}>{t('admin.discounts.expired')}</span>}
                    </td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        background: d.active && !expired ? theme.greenWash : theme.grey100,
                        color: d.active && !expired ? theme.greenDeep : theme.grey450,
                        padding: '3px 10px', borderRadius: 100, fontSize: 11, letterSpacing: '0.05em', fontWeight: 500
                      }}>
                        {expired ? t('admin.discounts.expired') : d.active ? t('admin.discounts.active') : t('admin.discounts.inactive')}
                      </span>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleToggle(d)} title={d.active ? t('admin.discounts.deactivateTitle') : t('admin.discounts.activateTitle')} style={{ background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '6px 10px', cursor: 'pointer', color: d.active ? theme.amber : theme.green, display: 'flex', alignItems: 'center' }}>
                          {d.active ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                        </button>
                        <button onClick={() => handleDelete(d)} title={t('admin.discounts.deleteTitle')} style={{ background: 'none', border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '6px 10px', cursor: 'pointer', color: theme.red, display: 'flex', alignItems: 'center' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: theme.alpha.black(0.5), zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: theme.white, borderRadius: 4, width: '100%', maxWidth: 480, padding: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
              <h2 style={{ fontFamily: theme.font.display, fontSize: 28, margin: 0 }}>{t('admin.discounts.modalTitle')}</h2>
              <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.grey400 }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Code */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={labelStyle}>{t('admin.discounts.labelCode')}</label>
                  <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} required placeholder={t('admin.discounts.codePlaceholder')} style={{ ...inputStyle, fontFamily: theme.font.mono, letterSpacing: '0.05em' }} />
                </div>

                {/* Type */}
                <div>
                  <label style={labelStyle}>{t('admin.discounts.labelType')}</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="percentage">{t('admin.discounts.typePercent')}</option>
                    <option value="fixed">{t('admin.discounts.typeFixed')}</option>
                  </select>
                </div>

                {/* Value */}
                <div>
                  <label style={labelStyle}>{form.type === 'percentage' ? t('admin.discounts.labelPercentage') : t('admin.discounts.labelAmount')}</label>
                  <input type="number" min="0.01" max={form.type === 'percentage' ? 100 : undefined} step="0.01" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} required placeholder={form.type === 'percentage' ? '10' : '5.00'} style={inputStyle} />
                </div>

                {/* Min rental days */}
                <div>
                  <label style={labelStyle}>{t('admin.discounts.labelMinDays')}</label>
                  <input type="number" min="1" value={form.min_rental_days} onChange={e => setForm({ ...form, min_rental_days: e.target.value })} style={inputStyle} />
                </div>

                {/* Max uses */}
                <div>
                  <label style={labelStyle}>{t('admin.discounts.labelMaxUses')}</label>
                  <input type="number" min="1" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" style={inputStyle} />
                </div>

                {/* Expires at */}
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={labelStyle}>{t('admin.discounts.labelExpiry')}</label>
                  <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} style={inputStyle} />
                </div>
              </div>

              {/* Preview */}
              {form.value && (
                <div style={{ background: theme.creamDeep, padding: '12px 16px', borderRadius: 4, marginTop: 16, fontSize: 13, color: theme.grey600 }}>
                  <Trans
                    i18nKey="admin.discounts.preview"
                    values={previewValues()}
                    components={{ strong: <strong style={{ color: theme.ink }} /> }}
                  />
                </div>
              )}

              <button type="submit" disabled={saving} style={{ width: '100%', marginTop: 24, padding: '16px', background: theme.ink, color: theme.white, border: 'none', borderRadius: 2, fontSize: 13, letterSpacing: '0.1em', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <Save size={16} /> {saving ? t('admin.discounts.creating') : t('admin.discounts.createCode')}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 12, color: theme.grey600, letterSpacing: '0.08em', marginBottom: 8 };
const inputStyle = { width: '100%', padding: '12px 16px', border: `1px solid ${theme.sand}`, borderRadius: 4, fontSize: 14, fontFamily: theme.font.body, boxSizing: 'border-box', background: theme.white };

